import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { useAuth } from './AuthContext';
import { isAuthError, isFatalError, handleWebSocketAuthError } from '@/shared/utils/websocket';
import { BACKEND_CONFIG } from '@/config/backendConfig';

interface PaymentData {
  payment_id: string;
  amount: number;
  refunded_amount: number;
  pending_approval_amount?: number;
  status: string;
  // Add other payment fields as needed
}

interface WebSocketMessage {
  type: 'refund_processed' | 'refund_approved' | 'refund_rejected' | 'refund_approval_requested' | 'payment_updated';
  data: {
    payment_id: string;
    payment: PaymentData;
  };
}

interface PaymentUpdatesContextType {
  subscribe: (callback: (message: WebSocketMessage) => void) => () => void;
  isConnected: boolean;
}

const PaymentUpdatesContext = createContext<PaymentUpdatesContextType | undefined>(undefined);

/**
 * Get WebSocket base URL dynamically based on current hostname.
 * This ensures multi-tenant support where each tenant uses their subdomain.
 */
const getWsBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const apiPort = BACKEND_CONFIG.apiPort; // Use configured port (8000 for Django, 8001 for FastAPI)

  // For localhost with subdomains (e.g., anandlab.localhost)
  if (hostname.includes('.localhost')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${hostname}:${apiPort}/ws`;
  }

  // For production, use environment variable
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // Fallback (should not be used in production)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  console.warn('⚠️ VITE_WS_URL not found! Falling back to constructed URL');
  return `${protocol}//${hostname}:${apiPort}/ws`;
};

// Singleton WebSocket manager to prevent multiple connections during React StrictMode
class WebSocketManager {
  private static instance: WebSocketManager | null = null;
  private ws: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | undefined;
  private shouldReconnect: boolean = true;
  private subscribers: Set<(message: WebSocketMessage) => void> = new Set();
  private connectionStateListeners: Set<(isConnected: boolean) => void> = new Set();
  private refCount: number = 0;
  private disconnectTimeout: NodeJS.Timeout | undefined;

  private constructor() {}

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  addRef(): void {
    this.refCount++;

    // Cancel any pending disconnect when a new component mounts
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = undefined;
    }

    if (this.refCount === 1 && (!this.ws || this.ws.readyState === WebSocket.CLOSED)) {
      this.shouldReconnect = true;
      this.connect();
    }
  }

  removeRef(): void {
    this.refCount--;
    if (this.refCount === 0) {
      // Delay disconnect to handle React StrictMode double-mounting
      // If another component mounts within 100ms, we won't disconnect
      this.disconnectTimeout = setTimeout(() => {
        this.disconnect();
        this.disconnectTimeout = undefined;
      }, 100);
    }
  }

  private connect(): void {
    // Don't connect if we're not supposed to
    if (!this.shouldReconnect) {
      return;
    }

    // Don't connect if already connected or connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Clear any pending reconnection attempts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      logger.warn('No access token found, cannot connect to WebSocket');
      return;
    }

    // Close existing connection if any - remove handlers to prevent reconnection
    if (this.ws) {
      const oldWs = this.ws;
      this.ws = null;
      // Remove all event handlers so they don't fire
      oldWs.onopen = null;
      oldWs.onmessage = null;
      oldWs.onerror = null;
      oldWs.onclose = null;
      oldWs.close();
    }

    try {
      // Get tenant from session storage or default to 'anandlab' for localhost
      const userDataString = localStorage.getItem('user');
      let tenant = 'anandlab'; // Default for localhost
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          tenant = userData.tenant_schema || 'anandlab';
        } catch (e) {
          logger.warn('Failed to parse user data for tenant', e);
        }
      }

      const wsBaseUrl = getWsBaseUrl();
      const ws = new WebSocket(`${wsBaseUrl}/payment-updates?token=${token}&tenant=${tenant}`);
      this.ws = ws;

      ws.onopen = () => {
        logger.debug('Payment updates WebSocket connected');
        this.notifyConnectionState(true);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          // Notify all subscribers
          this.subscribers.forEach(callback => {
            try {
              callback(message);
            } catch (err) {
              logger.error('Error in WebSocket message handler:', err);
            }
          });
        } catch (err) {
          logger.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        logger.error('Payment updates WebSocket error:', error);
      };

      ws.onclose = (event) => {
        logger.debug('Payment updates WebSocket disconnected', { code: event.code });
        console.log('[Payment WebSocket] Connection closed with code:', event.code, 'reason:', event.reason);

        // Check if close was due to auth error
        if (isAuthError(event.code)) {
          logger.warn('WebSocket closed due to authentication error');
          console.log('[Payment WebSocket] Detected auth error, calling handleWebSocketAuthError()');
          handleWebSocketAuthError();
          return; // Don't attempt reconnection
        }

        // Check if this is a fatal error (404, protocol error, etc.)
        if (isFatalError(event.code)) {
          logger.warn('WebSocket closed with fatal error code:', event.code);
          console.log('[Payment WebSocket] Fatal error detected, stopping reconnection attempts');
          this.shouldReconnect = false; // Stop trying to reconnect
        }

        this.notifyConnectionState(false);

        // Only clear if this is still the current connection
        if (this.ws === ws) {
          this.ws = null;
        }

        // Attempt to reconnect after 3 seconds if we should reconnect
        if (this.shouldReconnect) {
          this.reconnectTimeout = setTimeout(() => {
            logger.debug('Attempting to reconnect payment updates WebSocket...');
            this.connect();
          }, 3000);
        }
      };
    } catch (err) {
      logger.error('Failed to create payment updates WebSocket connection:', err);
    }
  }

  private disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.notifyConnectionState(false);
  }

  subscribe(callback: (message: WebSocketMessage) => void): () => void {
    this.subscribers.add(callback);
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  onConnectionStateChange(listener: (isConnected: boolean) => void): () => void {
    this.connectionStateListeners.add(listener);
    // Immediately notify with current state
    listener(this.ws?.readyState === WebSocket.OPEN);
    // Return unsubscribe function
    return () => {
      this.connectionStateListeners.delete(listener);
    };
  }

  private notifyConnectionState(isConnected: boolean): void {
    this.connectionStateListeners.forEach(listener => {
      try {
        listener(isConnected);
      } catch (err) {
        logger.error('Error in connection state listener:', err);
      }
    });
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export function PaymentUpdatesProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthReady, isAuthenticated } = useAuth();

  useEffect(() => {
    // Wait for auth to be ready before connecting
    if (!isAuthReady) {
      logger.debug('[PaymentUpdates] Waiting for auth to be ready...');
      return;
    }

    // Only connect if authenticated
    if (!isAuthenticated) {
      logger.debug('[PaymentUpdates] User not authenticated, skipping WebSocket connection');
      return;
    }

    // Don't connect WebSocket if user must change password first
    const mustChangePassword = localStorage.getItem('must_change_password');
    if (mustChangePassword === 'true') {
      logger.debug('[PaymentUpdates] Skipping WebSocket connection - user must change password first');
      return;
    }

    // Don't connect WebSocket for referral users - they don't have access to payment updates
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.user_type === 'referral' || user.user_type === 'referral_team') {
          logger.debug('[PaymentUpdates] Skipping WebSocket connection for referral portal user');
          return;
        }
      } catch (error) {
        logger.error('[PaymentUpdates] Failed to parse user data', error);
      }
    }

    logger.debug('[PaymentUpdates] Auth ready and user authenticated, connecting WebSocket...');

    const manager = WebSocketManager.getInstance();
    manager.addRef();

    const unsubscribeConnectionState = manager.onConnectionStateChange(setIsConnected);

    return () => {
      unsubscribeConnectionState();
      manager.removeRef();
    };
  }, [isAuthReady, isAuthenticated]);

  const subscribe = (callback: (message: WebSocketMessage) => void) => {
    const manager = WebSocketManager.getInstance();
    return manager.subscribe(callback);
  };

  return (
    <PaymentUpdatesContext.Provider value={{ subscribe, isConnected }}>
      {children}
    </PaymentUpdatesContext.Provider>
  );
}

export function usePaymentUpdates() {
  const context = useContext(PaymentUpdatesContext);
  if (!context) {
    throw new Error('usePaymentUpdates must be used within PaymentUpdatesProvider');
  }
  return context;
}
